import { FC, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useConvex, useQuery } from "convex/react";
import { useAppStore } from "@/store";
import { api } from "@/smart-views/client/api";
import { isConvexConfigured } from "@/smart-views/client/convex-client";
import { SMART_VIEW_ICON_KIND } from "@/smart-views/icons";
import { MDSIcon, MDSIconColor } from "@/mds";

interface CitedInBannerProps {
  noteId: string;
}

export const CitedInBanner: FC<CitedInBannerProps> = observer(function CitedInBanner({ noteId }) {
  const convex = useConvex();

  if (convex === undefined || !isConvexConfigured()) {
    return null;
  }

  return <CitedInBannerContent noteId={noteId} />;
});

const CitedInBannerContent: FC<CitedInBannerProps> = observer(function CitedInBannerContent({
  noteId,
}) {
  const { store } = useAppStore();
  const [expanded, setExpanded] = useState(false);

  const citations = useQuery(
    api.smartViews.client.findCurrentBlocksCitingNote,
    noteId ? { noteId } : "skip"
  );

  if (!citations || citations.length === 0) return null;

  // Collapse to one row per Smart View, with a count of how many blocks cite.
  const grouped = new Map<string, { title: string; blockCount: number }>();
  for (const c of citations) {
    const existing = grouped.get(c.smartViewId);
    if (existing) existing.blockCount += 1;
    else grouped.set(c.smartViewId, { title: c.smartViewTitle, blockCount: 1 });
  }
  const views = [...grouped.entries()];

  return (
    <Wrapper>
      <Toggle onClick={() => setExpanded(o => !o)}>
        <SmartViewIconSlot>
          <MDSIcon
            kind={SMART_VIEW_ICON_KIND}
            size={12}
            boxSize={14}
            color={MDSIconColor.Inherit}
          />
        </SmartViewIconSlot>
        <ToggleLabel>
          Cited in {views.length} Smart View{views.length === 1 ? "" : "s"}
        </ToggleLabel>
        <Caret $open={expanded}>▸</Caret>
      </Toggle>
      {expanded && (
        <List>
          {views.map(([smartViewId, info]) => (
            <Row key={smartViewId} onClick={() => store.navigation.goToSmartView({ smartViewId })}>
              <RowView>{info.title}</RowView>
              <RowMeta>
                {info.blockCount} block{info.blockCount === 1 ? "" : "s"}
              </RowMeta>
            </Row>
          ))}
        </List>
      )}
    </Wrapper>
  );
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: ${({ theme }) => theme.spacing["3"]} ${({ theme }) => theme.spacing["6"]};
  background: ${({ theme }) => theme.colors.background.button.onChrome.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  align-self: flex-start;
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const SmartViewIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.icon.secondary};
`;

const ToggleLabel = styled.span``;

const Caret = styled.span<{ $open: boolean }>`
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
  font-size: 10px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface.pane};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: inherit;
  &:hover {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  }
`;

const RowView = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const RowMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;
